/**
 * 「広域避難場所に準じる屋外の広い避難場所」の判定。
 *
 * 避難場所の自動選定は長らく **広域避難場所カテゴリだけ** を探していた。
 * ところが千代田区のように区内が全域 (115町丁目すべて) 地区内残留地区で、
 * 広域避難場所が1つも指定されていない区がある。そこでは自動選定が区外まで探しに行き、
 * 589m の北の丸公園を飛ばして 1,588m の後楽園一帯 (文京区) を選んでいた。
 *
 * 北の丸公園はマスターに入っているが種別が **指定緊急避難場所** (区が災害対策基本法で
 * 指定) で、広域避難場所 (都が大火災用に指定) ではないため候補から外れていた。
 *
 * ただし「指定緊急避難場所の最寄り」に広げるだけでは駄目で、近いのは小さな屋内施設に
 * なる (実測: 赤坂の最寄りは サン・サン赤坂 2,666m²、田町は 芝浦アイランド児童高齢者
 * 交流プラザ 1,017m²)。避難場所に必要なのは**広さ**なので面積で絞る。
 */
/**
 * 屋外の広い避難場所と見なす最小面積 (m²)。
 *
 * 実測 (地区内残留地区10件の近傍候補) で、採用したいものと外したいものは
 * この値できれいに分かれた:
 *   採用: 旧蒲田電車区周辺一帯 46,746 / 新川ツインビル地区 64,354 / 駒場地区 68,420 /
 *         迎賓館一帯 88,773 / 有栖川宮記念公園一帯 90,373 / 萩中公園 171,920 /
 *         北の丸公園 198,436 / 青山墓地一帯 373,094 / 後楽園一帯 397,578
 *   除外: 芝浦アイランド児童高齢者交流プラザ 1,017 / サン・サン赤坂 2,666 /
 *         日本大学会館 3,237 / 三田図書館 7,093 / 菅刈小学校及び菅刈公園 8,488 /
 *         スポーツセンター 10,886 / 日本大学通信教育学部 13,539
 * 20,673 (真田堀運動場) と 46,746 の間が空いているので、その間に置く。
 */
export const OPEN_SPACE_MIN_AREA_M2 = 30_000;

/**
 * 屋外の広い避難場所を候補にできる上限距離 (m)。
 *
 * この規則は「近くに広域避難場所が無い地域」の救済なので、遠すぎる候補まで拾うと
 * かえって害になる。実測: 上限なしだと横浜の3物件が 5.6〜16.6km 先の東京の公園
 * (多摩川河川敷・ガス橋一帯 11.9km / 鶴間公園 16.6km) を指した。
 * 区の指定でも 2.6km (学芸大学→駒沢オリンピック公園一帯) が最遠なので、その少し上。
 */
export const OPEN_SPACE_MAX_DIST_M = 3_000;

/**
 * 大火災の指定が無い広場で、指定のある広域避難場所を置き換えてよい距離の比。
 *
 * この救済は「指定はあるが遠い」ときのためのもの。ところが川崎の武蔵小杉で、
 * **地震のみ指定**の中原平和公園 (1,237m) が、大規模な火事の指定を持つ
 * 等々力緑地 (1,263m) をわずか26mの差で押しのけていた。避難場所は大火災から
 * 逃れる場所なので、指定の無い広場が勝ってよいのは「明らかに近い」ときだけにする。
 * 千代田区の北の丸公園 (589m) 対 後楽園一帯 (1,588m) は 0.37 倍で従来どおり通る。
 */
export const OPEN_SPACE_OVERRIDE_RATIO = 0.7;

export type Ring = Array<[number, number]>;

/** 判定に必要な最小限の形 (マスターの EvacuationPlace から取り出す)。 */
export interface OpenSpaceCandidate {
  /** 災害種別 (「大火災」を含むかで置き換えの条件が変わる)。 */
  disasterTypes?: string[];
  categories?: string[];
  category?: string;
  /** 暫定円 (approxShape) は実面積を表さないので対象外。 */
  approxShape?: boolean;
  polygon?: { coordinates: Ring[] };
}

/** リングの面積 (m²)。緯度で経度をスケールした平面近似 (靴ひも公式)。 */
export function ringAreaM2(ring: Ring): number {
  if (!ring || ring.length < 4) return 0;
  const lat0 = ring[0]![1];
  const kx = Math.cos((lat0 * Math.PI) / 180) * 111_320;
  const ky = 110_574;
  let a = 0;
  for (let i = 0; i < ring.length - 1; i += 1) {
    const x1 = ring[i]![0] * kx;
    const y1 = ring[i]![1] * ky;
    const x2 = ring[i + 1]![0] * kx;
    const y2 = ring[i + 1]![1] * ky;
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2;
}

/**
 * 避難場所として使える「屋外の広い場所」か。
 *
 * - 指定緊急避難場所であること (区が避難先として指定している)
 * - **指定避難所ではない**こと — 指定避難所は生活避難の屋内施設で、地図では
 *   避難所欄に出す。避難場所欄に同じ施設を出すと2枠が重複する
 * - 実形状を持ち、面積が OPEN_SPACE_MIN_AREA_M2 以上であること
 */
export function isWideOpenSpace(place: OpenSpaceCandidate): boolean {
  const cats = place.categories ?? (place.category ? [place.category] : []);
  if (!cats.includes('指定緊急避難場所')) return false;
  if (cats.includes('指定避難所')) return false;
  if (place.approxShape) return false;
  const rings = place.polygon?.coordinates ?? [];
  if (rings.length === 0) return false;
  const area = rings.reduce((m, r) => Math.max(m, ringAreaM2(r)), 0);
  return area >= OPEN_SPACE_MIN_AREA_M2;
}

/**
 * 避難場所の自動選定。距離昇順に並んだ候補から、
 * 「広域避難場所」と「屋外の広い避難場所」のうち**近いほう**を返す。
 *
 * 広域避難場所が候補に1つも無い地域では null を返し、従来の規則 (大火災対応 → 最寄り)
 * に委ねる。ここで屋外の広い場所を「作り出す」と、広域避難場所の指定が無い地域
 * (横浜など) で今まで出ていなかった施設に変わってしまうため
 * (実測: 鶴見Ⅱ が 末吉中学校 2,121m → 横浜市立生麦中学校 校舎１３ 2,280m)。
 * この規則はあくまで「指定はあるが遠い」ときの置き換えに限る。
 */
export function pickEvacuationArea<
  T extends { p: OpenSpaceCandidate & { name?: string }; d: number },
>(nearSorted: T[]): T | null {
  const koiki = nearSorted.find((x) => {
    const cats = x.p.categories ?? (x.p.category ? [x.p.category] : []);
    return cats.includes('広域避難場所');
  });
  // 比較の相手は「大火災の指定がある最寄り」。広域避難場所カテゴリだけを見ると、
  // 神奈川のように広域避難場所が別カテゴリ (指定緊急避難場所) で登録されている
  // 地域で、遠くの無関係な広域避難場所 (実測: 武蔵小杉から7km先の横浜の公園) と
  // 比べてしまい、1.3km の等々力緑地 (大火災指定) を素通りしていた。
  const fire = nearSorted.find((x) =>
    (x.p.disasterTypes ?? []).includes('大火災'),
  );
  const ref =
    fire && koiki ? (fire.d <= koiki.d ? fire : koiki) : (fire ?? koiki);
  if (!ref) return null; // 大火災の指定が近くに無い地域は従来の規則に委ねる
  const open = nearSorted.find(
    (x) => x.d <= OPEN_SPACE_MAX_DIST_M && isWideOpenSpace(x.p),
  );
  if (!open || open.d >= ref.d) return ref;
  // 大火災の指定がある広場は目的が同じなので近いほうでよい。
  // 指定が無い広場は「明らかに近い」ときだけ置き換える。
  const openFire = (open.p.disasterTypes ?? []).includes('大火災');
  return openFire || open.d <= ref.d * OPEN_SPACE_OVERRIDE_RATIO ? open : ref;
}
